import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import referralAnalytics from '~/analytics/referral/referralAnalytics'
import { ButtonPrimary } from '~/components/Buttons'
import { InputV2 } from '~/components/InputV2'
import SuccessPopup from '~/components/Referral/components/successPopup'
import Loader from '~/components/Utility/Loader'
import { REWARD_FEATURE } from '~/gamification/types/gamification.types'
import { useSetRewardsMutation } from '~/redux/apis/reward-api'
import { useSendReferralEmailMutation } from '~/redux/apis/user-api'
import { validateEmail } from '~/utils/helpers'

export default function ReferralPage(): React.ReactElement {
  const { t } = useTranslation('referral')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [sendingReferral, setSendingReferral] = useState(false)
  const [showSuccessPopup, setShowSuccessPopup] = useState(false)
  const [sendReferralEmail] = useSendReferralEmailMutation()
  const [setRewards] = useSetRewardsMutation()
  const disableButton = Boolean(errorMessage || sendingReferral)

  useEffect(() => {
    if (email && !isInputFocused) {
      const isValid = validateEmail(email)
      if (!isValid) {
        setErrorMessage(t('error.invalidEmail'))
      } else {
        setErrorMessage(undefined)
      }
    }
  }, [email, isInputFocused, t])

  const handleSendInvite = async (): Promise<void> => {
    referralAnalytics.logCTAClick('referral_cta_button', t('sendInvite'))
    if (!name || !email) {
      setErrorMessage(t('error.requiredField'))
      return
    } else if (!validateEmail(email)) {
      setErrorMessage(t('error.invalidEmail'))
      return
    } else {
      setSendingReferral(true)
      setErrorMessage(undefined)
      try {
        await sendReferralEmail({ email, name }).unwrap()
        referralAnalytics.logReferralEmailSent()
        setRewards({ xp: 250, feature: REWARD_FEATURE.REFERRAL, event_id: 'referral#emailSent' })
        setShowSuccessPopup(true)
        setSendingReferral(false)
      } catch (error) {
        setErrorMessage(t('error.sendingFailed'))
        setSendingReferral(false)
        return
      }
      setEmail('')
      setName('')
    }
  }

  const onBlur = (): void => {
    setIsInputFocused(false)
    if (validateEmail(email) && name) {
      setErrorMessage(undefined)
    }
  }

  const onFocus = (): void => {
    setIsInputFocused(true)
  }

  return (
    <div className="flex flex-col gap-2.5 text-white max-w-125">
      {showSuccessPopup && <SuccessPopup />}
      <h1 className="text-6 lg:text-8 font-bold text-center lg:text-left">{t('shareMagic')}</h1>
      <div className="flex flex-col gap-2.5 bg-purple-darker border border-white rounded p-3 shadow-lg">
        <p>{t('notAlone')}</p>
        <p>{t('inviteFriends')}</p>
      </div>
      <div className="bg-white rounded text-black p-3 drop-shadow-white-sm">
        <h3 className="text-md font-bold leading-7">{t('friendGets')}</h3>
        <ul className="list-disc pl-6">
          <li>{t('getsList.goldenTicket')}</li>
          <li>{t('getsList.funTools')}</li>
          <li>{t('getsList.fastMastery')}</li>
        </ul>
      </div>
      <div>
        <p>{t('to')}</p>
        <InputV2
          autoComplete="email"
          className={`${
            (!email || !validateEmail(email)) && errorMessage ? 'border-red-error' : 'border-gray-darker'
          } border-2 `}
          label={t('dropEmail')}
          name="recipientEmail"
          onBlur={onBlur}
          onFocus={onFocus}
          setValue={setEmail}
          type="email"
          value={email}
        />
      </div>
      <div>
        <p>{t('from')}</p>
        <InputV2
          autoComplete="name"
          className={`${
            !name && errorMessage && validateEmail(email) ? 'border-red-error' : 'border-gray-darker'
          } border-2 `}
          label={t('name')}
          name="senderName"
          onBlur={onBlur}
          onFocus={onFocus}
          setValue={setName}
          type="text"
          value={name}
        />
      </div>
      {errorMessage && (
        <p
          className={`bg-white rounded text-red-error px-2 py-1 border-2 border-red-error text-center overflow-hidden ${
            !isInputFocused ? 'opacity-100 h-auto' : 'opacity-0 h-0'
          } duration-500`}
        >
          {errorMessage}
        </p>
      )}
      <ButtonPrimary className="border-2 border-white py-3" disabled={disableButton} onClick={handleSendInvite}>
        <Loader color="white" isLoading={sendingReferral}>
          <>{t('sendInvite')}</>
        </Loader>
      </ButtonPrimary>
    </div>
  )
}
